/* eslint-disable @stripe-internal/stripe/no-mixed-react-exports */
import {addNonsensitiveMetadataToSchema} from 'src/schema/nonsensitive';
import {getNonSensitiveSchemas} from 'src/schema/valibot/get-nonsensitive-schema';
import {custom} from 'valibot';

import type {StandardSchemaV1} from '@standard-schema/spec';
import type {NONSENSITIVE} from 'src/schema/types';
import type {BaseIssue, BaseSchema} from 'valibot';

/**
 * valibotNonsensitive is a helper function to annotate valibot schema fields as nonsensitive.
 * By default, all fields are considered sensitive.
 * This annotation is used to identify fields that can be safely shared with untrusted contexts.
 * It should only be used on leaf/primitive fields of a schema. Using it on complex objects will result in a runtime error.
 *
 * Pass `{ UNSAFE_allowNonsensitiveObject: true }` to disable the object check and allow complex
 * objects to be marked as nonsensitive. Only use this when you are sure all nested fields are
 * safe to share.
 *
 * @example
 * import {object, string, pipe, valibotNonsensitive as nonsensitive} from '@sail/comms';
 * const userSchema = object({
 *   id: pipe(string(), nonsensitive()), // This field is marked as nonsensitive
 *   secretToken: string(), // This field is sensitive
 * });
 *
 * @example
 * import {object, pipe, valibotNonsensitive as nonsensitive} from '@sail/comms';
 * const schema = object({
 *   metadata: pipe(object({ key: string() }), nonsensitive({ UNSAFE_allowNonsensitiveObject: true })),
 * });
 */
export function valibotNonsensitive<T>(params?: {
  UNSAFE_allowNonsensitiveObject?: boolean;
}): BaseSchema<T & NONSENSITIVE, T & NONSENSITIVE, BaseIssue<unknown>> {
  const s = custom<T & NONSENSITIVE>(
    (input) =>
      params?.UNSAFE_allowNonsensitiveObject === true ||
      input?.constructor.name.toLowerCase() !== 'object',
    'nonsensitive annotation should only be used on leaf/primitive fields',
  );

  return addNonsensitiveMetadataToSchema(s) as typeof s;
}

export const valibotRetrieveNonsensitiveOnlySchema = <
  TSchema extends StandardSchemaV1,
>(
  schema: TSchema,
): BaseSchema<unknown, unknown, BaseIssue<unknown>> | undefined => {
  // Check for the presence of the nonsensitive annotation and filter out the rest
  return getNonSensitiveSchemas(
    schema as unknown as BaseSchema<unknown, unknown, BaseIssue<unknown>>,
  );
};
